import { Modal } from '@klear/ui/components/modal'
import { DatePicker } from '@klear/ui/components/date-picker'
import { Label } from '@klear/ui/components/label'
import { KlearDate } from '@klear/date'
import { useState } from 'react'
import { trpc } from '@klear/ui/lib/trpc'
import { toast } from '@klear/ui/hooks/use-toast'
import { useOrderEventInvalidate } from '@klear/ui/hooks/use-order-event-invalidate'
import isError from 'lodash/isError'

interface MarkFundedModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
  offerId: number
  offerIdDisplay: string
}

export function MarkFundedModal({ isOpen, onClose, onSuccess, offerId, offerIdDisplay }: MarkFundedModalProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [fundedAt, setFundedAt] = useState<KlearDate>(KlearDate.today())
  const invalidateOrderEvents = useOrderEventInvalidate()

  const setFundedAtMutation = trpc.invoiceOffer.setOfferFundedAt.useMutation()

  const handleSubmit = async () => {
    setIsLoading(true)
    try {
      await setFundedAtMutation.mutateAsync({
        offerId,
        fundedAt: fundedAt.toJSON()
      })
      await invalidateOrderEvents()
      toast({
        title: 'Offer marked as funded',
        description: `${offerIdDisplay} has been marked as funded.`
      })
      onSuccess?.()
      onClose()
    } catch (error) {
      toast({
        title: 'Error',
        description: isError(error) ? error.message : 'Failed to mark offer as funded',
        variant: 'destructive'
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal
      size="sm"
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      title="Mark Offer as Funded"
      description={`Set the funding date for ${offerIdDisplay}.`}
      onSubmit={handleSubmit}
      primaryText={isLoading ? 'Processing...' : 'Mark Funded'}
      primaryDisabled={isLoading}
      secondaryText="Cancel"
      secondaryDisabled={isLoading}
      handleSecondaryAction={onClose}
      handlePrimaryAction={handleSubmit}
    >
      <div className="space-y-2">
        <Label>Funded At</Label>
        <DatePicker value={fundedAt} onChange={(date) => date && setFundedAt(date)} disabled={isLoading} />
      </div>
    </Modal>
  )
}
